import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { ChatAttachment } from '@backrs_ui/common-ui/src/molecules/Lubav/InputBox';

import { Box, Chip, SxProps, Theme, Tooltip } from '@mui/material';

type AttachedFileChipProps = Readonly<{
  filename: string;
  status?: ChatAttachment['uploadStatus'];
  onRemove?: () => void;
}>;

export const AttachedFileChip = ({ filename, status, onRemove }: AttachedFileChipProps) => {
  const isUploading = status === 'uploading';
  const hasError = status === 'error';

  const chip = (
    <Chip
      variant="badge"
      size="md"
      type="leading"
      label={isUploading ? `${filename}...` : filename}
      icon={
        hasError ? (
          <AppIcon iconName="AlertCircle" diameter="12px" color="Error/600" />
        ) : (
          <AppIcon iconName="File06" diameter="12px" color="Colors/Foreground/fg-quaternary" />
        )
      }
      sx={chipSx(status)}
      onDelete={!isUploading && onRemove ? onRemove : undefined}
      deleteIcon={
        <Box component="span" sx={deleteIconSx}>
          <AppIcon iconName="XClose" diameter="12px" color="Gray/500" />
        </Box>
      }
    />
  );

  if (hasError) {
    return (
      <Tooltip title="Upload failed — remove and try again" arrow>
        {chip}
      </Tooltip>
    );
  }

  return chip;
};

const chipSx =
  (status?: ChatAttachment['uploadStatus']): SxProps<Theme> =>
  ({ backrs, typography }) => ({
    backgroundColor:
      status === 'error' ? backrs.colors['Error/50'] : backrs.colors['Colors/Base/White'],
    border: `1px solid ${
      status === 'error'
        ? backrs.colors['Error/600']
        : backrs.colors['Colors/Border/border-primary']
    }`,
    paddingLeft: backrs.spacing['spacing-sm'],
    paddingRight: backrs.spacing['spacing-sm'],
    gap: backrs.spacing['spacing-xs'],
    opacity: status === 'uploading' ? 0.6 : 1,
    '& .MuiChip-label': {
      ...typography['text-xs-regular'],
      color: backrs.colors['Gray/700'],
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      padding: 0,
    },
    '& .MuiChip-icon': { margin: 0 },
    '& .MuiChip-deleteIcon': { margin: 0 },
  });

const deleteIconSx: SxProps<Theme> = {
  display: 'flex',
  cursor: 'pointer',
};
